import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowRightLeft } from 'lucide-react';
import { useSpaceStore } from '../stores/spaceStore';
import { useExpenseStore } from '../stores/expenseStore';
import { useCategoryStore } from '../stores/categoryStore';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Skeleton, { SkeletonList } from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState';
import ExpenseEditSheet from '../components/ExpenseEditSheet';
import { supabase } from '../lib/supabase';
import type { ExpenseActivityItem, RecentActivityItem } from '../types/activity';

interface MemberBalance {
  id: string;
  display_name: string;
  avatar_emoji: string;
  balance_cents: number;
}

interface HomeSummary {
  current_member_id: string;
  current_member_balance_cents: number;
  member_balances: MemberBalance[];
  is_settled: boolean;
  recent_activity: RecentActivityItem[];
}

export default function Home() {
  const navigate = useNavigate();
  const { currentSpace, members, fetchMembers } = useSpaceStore();
  const {
    expenses,
    settlements,
    fetchExpenses,
    fetchSettlements,
    subscribeToChanges,
    setPrefill,
  } = useExpenseStore();
  const { categories, fetchCategories } = useCategoryStore();
  const [isLoading, setIsLoading] = useState(true);
  const [summary, setSummary] = useState<HomeSummary | null>(null);
  const [editingExpense, setEditingExpense] = useState<ExpenseActivityItem | null>(null);

  const fetchHomeSummary = async (spaceId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`/api/dashboard/home/${spaceId}`, {
      headers: {
        ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
      },
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(payload.error || 'Failed to fetch home summary');
    }

    setSummary(payload);
  };

  useEffect(() => {
    let isCancelled = false;

    const loadData = async () => {
      if (!currentSpace?.id) {
        if (!isCancelled) {
          setIsLoading(false);
          setSummary(null);
        }
        return;
      }

      if (!isCancelled) {
        setIsLoading(true);
      }

      try {
        await Promise.all([
          fetchExpenses(currentSpace.id),
          fetchSettlements(currentSpace.id),
          fetchMembers(currentSpace.id),
          fetchCategories(currentSpace.id),
          fetchHomeSummary(currentSpace.id),
        ]);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isCancelled = true;
    };
  }, [currentSpace?.id]);

  useEffect(() => {
    if (!currentSpace?.id) {
      return undefined;
    }

    const unsubscribe = subscribeToChanges(currentSpace.id);
    return () => unsubscribe();
  }, [currentSpace?.id]);

  useEffect(() => {
    if (!currentSpace?.id) {
      return;
    }

    fetchHomeSummary(currentSpace.id).catch((error) => {
      console.error('Failed to refresh home summary:', error);
    });
  }, [currentSpace?.id, expenses, settlements]);

  const formatCurrency = (cents: number) =>
    `${cents < 0 ? '-' : ''}$${(Math.abs(cents) / 100).toFixed(2)}`;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const currentBalance = summary?.current_member_balance_cents || 0;
  const currentMemberId = summary?.current_member_id || null;

  const topCounterparty = useMemo(() => {
    if (!summary || currentBalance === 0) {
      return null;
    }

    const targetSign = currentBalance > 0 ? -1 : 1;
    return summary.member_balances
      .filter((member) => member.id !== currentMemberId && Math.sign(member.balance_cents) === targetSign)
      .reduce<MemberBalance | null>((currentTop, member) => {
        if (!currentTop || Math.abs(member.balance_cents) > Math.abs(currentTop.balance_cents)) {
          return member;
        }

        return currentTop;
      }, null);
  }, [summary, currentBalance, currentMemberId]);

  const today = new Date().getDate();
  const dueSoonFixed = categories
    .filter((category) => category.is_fixed && category.fixed_configs?.[0] && !category.fixed_configs[0].is_paused)
    .map((category) => ({
      ...category,
      config: category.fixed_configs![0],
    }))
    .filter((category) => {
      const due = category.config.due_day;
      const diff = due >= today ? due - today : 31 - today + due;
      return category.config.autopay === false && diff <= 7;
    });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton width="100%" height={120} className="rounded-2xl" />
        <Skeleton width="60%" height={24} className="rounded" />
        <SkeletonList count={3} />
      </div>
    );
  }

  if (!currentSpace) {
    return (
      <EmptyState
        icon="🏠"
        title="No Space Yet"
        description="Create a space to start tracking expenses with your partner or group"
        action={{
          label: 'Create Space',
          onClick: () => navigate('/onboarding/space'),
        }}
      />
    );
  }

  return (
    <div className="space-y-6 pb-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{currentSpace.name}</h1>
          <p className="text-muted text-sm">
            {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>

      <Card className="bg-gradient-to-br from-primary/20 to-primary/5 border-primary/20">
        <div className="text-center py-4">
          <p className="text-muted text-sm mb-1">Shared Balance</p>
          {currentBalance !== 0 ? (
            <>
              <p className={`text-4xl font-bold tabular-nums ${
                currentBalance > 0 ? 'text-primary' : 'text-danger'
              }`}>
                {formatCurrency(Math.abs(currentBalance))}
              </p>
              <p className="text-sm text-muted mt-1">
                {currentBalance > 0
                  ? (topCounterparty
                    ? `${topCounterparty.display_name} owes you`
                    : 'You are owed across the space')
                  : (topCounterparty
                    ? `You owe ${topCounterparty.display_name}`
                    : 'You owe across the space')}
              </p>
            </>
          ) : (
            <>
              <p className="text-4xl font-bold tabular-nums text-primary">$0.00</p>
              <p className="text-sm text-muted mt-1">All settled up</p>
            </>
          )}
        </div>
      </Card>

      {dueSoonFixed.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-foreground">Upcoming Fixed Expenses</h2>
          {dueSoonFixed.map(({ config, ...category }) => {
            const due = config.due_day;
            const isToday = due === today;
            return (
              <Card
                key={category.id}
                interactive
                onClick={() => {
                  setPrefill({ categoryId: category.id, amountCents: config.amount_cents });
                  navigate('/log');
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{category.emoji}</span>
                    <div>
                      <p className="font-medium">{category.name}</p>
                      <p className="text-sm text-muted">
                        {isToday ? 'Due today' : `Due day ${due}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold tabular-nums">
                      {formatCurrency(config.amount_cents)}
                    </p>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        setPrefill({ categoryId: category.id, amountCents: config.amount_cents });
                        navigate('/log');
                      }}
                      className="text-xs font-medium text-primary mt-1"
                    >
                      Log Now
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-foreground">Recent Activity</h2>
          <button
            onClick={() => navigate('/dashboard/shared')}
            className="text-primary text-sm font-medium flex items-center gap-1"
          >
            View All <ArrowRight size={16} />
          </button>
        </div>

        {summary?.recent_activity && summary.recent_activity.length > 0 ? (
          <div className="space-y-3">
            {summary.recent_activity.slice(0, 5).map((activity, index) => {
              const canEditExpense = activity.type === 'expense' && activity.logged_by === currentMemberId;

              return (
                <motion.div
                  key={`${activity.type}-${activity.id}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card
                    interactive={canEditExpense}
                    onClick={canEditExpense ? () => setEditingExpense(activity) : undefined}
                  >
                    {activity.type === 'expense' ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{activity.category.emoji || '📦'}</span>
                          <div>
                            <p className="font-medium">{activity.category.name || 'Uncategorized'}</p>
                            <p className="text-sm text-muted">
                              {activity.paid_by_member.display_name} · {formatDate(activity.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold tabular-nums">
                            {formatCurrency(activity.amount_cents)}
                          </p>
                          <Badge
                            variant={activity.is_fixed_autolog ? 'fixed' : activity.action === 'split' ? 'split' : 'keep'}
                            size="sm"
                          >
                            {activity.is_fixed_autolog ? 'FIXED' : activity.action.toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2 text-lg">
                            <span>{activity.from_member_details.avatar_emoji}</span>
                            <ArrowRightLeft size={16} className="text-primary" />
                            <span>{activity.to_member_details.avatar_emoji}</span>
                          </div>
                          <div>
                            <p className="font-medium">
                              {activity.from_member_details.display_name} paid {activity.to_member_details.display_name}
                            </p>
                            <p className="text-sm text-muted">
                              Settlement · {formatDate(activity.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold tabular-nums text-primary">
                            {formatCurrency(activity.amount_cents)}
                          </p>
                          <Badge variant="split" size="sm">SETTLED</Badge>
                        </div>
                      </div>
                    )}
                  </Card>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <EmptyState
            icon="📝"
            title="No Activity Yet"
            description="Start logging expenses to track your shared spending"
            action={{
              label: 'Log First Expense',
              onClick: () => navigate('/log'),
            }}
          />
        )}
      </div>

      <ExpenseEditSheet
        expense={editingExpense}
        isOpen={!!editingExpense}
        onClose={() => setEditingExpense(null)}
      />
    </div>
  );
}
